import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IMigratedDataSummary,
  ISourceEntityHandler,
  ISourceEntityTypes,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
  type ISourceEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { Brand, hasMergeConflicts } from '@principle-theorem/principle-core';
import {
  type IBrand,
  type IContact,
  type IReferralSourceConfiguration,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  PrimaryKey,
  asDocRef,
  toTimestamp,
  type WithRef,
  FirestoreMigrate,
} from '@principle-theorem/shared';
import { Observable, from, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslationMapHandler } from '../../translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';
import { DestinationEntity } from '../destination-entity';

export const CONTACT_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: 'contacts',
    label: 'Contacts',
    description: '',
  },
});

export interface IContactDestinationRecord {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  contactRef: DocumentReference<IContact | IReferralSourceConfiguration>;
}

export interface IContactErrorData {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
}

export interface IContactMigrationData {
  sourceContactId: string | number;
  contact: IContact;
}

export interface IContactJobData<ContactRecord extends ISourceEntityTypes> {
  sourceContact: IGetRecordResponse<ContactRecord>;
  brand: WithRef<IBrand>;
}

export abstract class BaseContactDestinationEntity<
  ContactRecord extends ISourceEntityTypes,
  JobData extends IContactJobData<ContactRecord>,
> extends BaseDestinationEntity<
  IContactDestinationRecord,
  JobData,
  IContactMigrationData
> {
  destinationEntity = CONTACT_DESTINATION_ENTITY;
  abstract contactSourceEntity: ISourceEntityHandler<ContactRecord>;

  get sourceCountComparison(): ISourceEntityHandler<ContactRecord> {
    return this.contactSourceEntity;
  }

  sourceCountDataAccessor(
    data: IContactJobData<ContactRecord>
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourceContact.record.id;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<IContactDestinationRecord>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return from(Firestore.getDoc(record.data.contactRef)).pipe(
      map((contact) => [
        {
          label: 'Contact',
          data: contact,
        },
      ])
    );
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: IContactJobData<ContactRecord>,
    migrationData: IContactMigrationData
  ): Promise<IDestinationEntityRecord> {
    const existingContactRef = await translationMap.getDestination(
      migrationData.sourceContactId.toString(),
      this.contactSourceEntity.sourceEntity.metadata.idPrefix
    );

    const contactRef = await FirestoreMigrate.upsertDoc(
      Brand.contactCol(jobData.brand),
      {
        ...migrationData.contact,
        createdAt: toTimestamp(),
      },
      existingContactRef?.id
    );

    if (!existingContactRef) {
      await translationMap.upsert({
        sourceIdentifier: migrationData.sourceContactId.toString(),
        destinationIdentifier: contactRef,
        resourceType: this.contactSourceEntity.sourceEntity.metadata.idPrefix,
      });
    }

    return this._buildSuccessResponse(jobData.sourceContact, contactRef);
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: IContactMigrationData
  ): Promise<IContactMigrationData | undefined> {
    const existingContactRef = await translationMap.getDestination(
      data.sourceContactId.toString(),
      this.contactSourceEntity.sourceEntity.metadata.idPrefix
    );
    if (!existingContactRef) {
      return;
    }

    try {
      const existingContact = await Firestore.getDoc(
        asDocRef<IContact>(existingContactRef)
      );
      const hasMergeConflict = hasMergeConflicts(data.contact, existingContact);
      if (hasMergeConflict) {
        return {
          ...data,
          contact: existingContact,
        };
      }
    } catch {
      //
    }
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IContactJobData<ContactRecord>,
    _migrationData: IContactMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourceContact.record.uid,
      label: jobData.sourceContact.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourceContact.record.id,
    };
  }

  getDestinationEntityRecordUid(data: IContactJobData<ContactRecord>): string {
    return data.sourceContact.record.uid;
  }

  buildSkippedResponse(
    data: JobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourceContact.record.uid,
      label: data.sourceContact.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourceContact.record.id,
    };
  }

  protected _buildErrorResponse(
    contact: IGetRecordResponse<ContactRecord>,
    message?: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: contact.record.uid,
      label: contact.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: contact.record.id,
      errorMessage: message ?? 'Missing required contact properties',
      failData: {
        contactRef: contact.record.id,
      },
    };
  }

  private _buildSuccessResponse(
    record: IGetRecordResponse<ContactRecord>,
    contactRef: DocumentReference<IContact | IReferralSourceConfiguration>
  ): IDestinationEntityRecord<IContactDestinationRecord> {
    return {
      uid: record.record.uid,
      label: record.record.label,
      sourceRef: record.record.id,
      data: {
        sourceRef: record.record.id,
        contactRef,
      },
      status: DestinationEntityRecordStatus.Migrated,
      migratedAt: toTimestamp(),
    };
  }
}
